import { useQuery } from "@tanstack/react-query";
import { apiClient, healthKeys } from "../api";

export function useHealthStatus() {
  // First check liveness (fast, lightweight check)
  const {
    data: livenessData,
    isLoading: isLivenessLoading,
    isError: isLivenessError,
  } = useQuery({
    queryKey: healthKeys.liveness(),
    queryFn: async () => {
      const baseUrl =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:5000";
      console.log("[Liveness Check] Fetching from:", `${baseUrl}/livez`);

      try {
        const result = await apiClient.checkLiveness();
        console.log("[Liveness Check] Response:", result);
        return result;
      } catch (err) {
        console.error("[Liveness Check] Failed:", err);
        throw err;
      }
    },
    refetchInterval: 30000,
    retry: 1,
    staleTime: 10000,
  });

  // Then check readiness (includes version and detailed status)
  const {
    data: readinessData,
    isLoading: isReadinessLoading,
    isError: isReadinessError,
    error,
  } = useQuery({
    queryKey: healthKeys.readiness(),
    queryFn: async () => {
      const baseUrl =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:5000";
      console.log("[Readiness Check] Fetching from:", `${baseUrl}/readyz`);

      try {
        const result = await apiClient.checkReadiness();
        console.log("[Readiness Check] Success:", result);
        return result;
      } catch (err) {
        console.error("[Readiness Check] Failed:", err);
        throw err;
      }
    },
    enabled: !!livenessData, // Only check readiness after liveness succeeds
    refetchInterval: 30000,
    retry: 1,
    staleTime: 10000,
  });

  const isAlive =
    livenessData?.status === "alive" || livenessData?.status === "Healthy";
  const isReady =
    readinessData?.status === "ready" || readinessData?.status === "Healthy";

  return {
    // Liveness state
    isAlive,
    isLivenessLoading,
    isLivenessError,
    // Readiness state
    isReady,
    isReadinessLoading,
    isReadinessError,
    error,
    // Combined state for backward compatibility
    isHealthy: isAlive && isReady,
    isLoading: isLivenessLoading || isReadinessLoading,
    isError: isLivenessError || isReadinessError,
    // Data
    version: readinessData?.version,
    providerCount: readinessData?.providerCount,
    tldCount: readinessData?.tldCount,
    tldDataAge: readinessData?.tldDataAge,
    tldDataTimestamp: readinessData?.tldDataTimestamp,
  };
}
